import type {
  SocialDateRange,
  SocialMetricSeries,
  SocialOverview,
  SocialPlatform,
} from '../../types/socialReporting.js';

// Organic Social Reporting — the Overview trend EXPLORER (pure).
//
// A multi-metric × multi-platform series builder for the ComposedChart. Draws one
// line/bar/area per ACTIVE metric × ACTIVE platform, but ONLY where a daily series
// genuinely exists — the availability matrix below is the gate, the same
// graceful-degradation discipline as the GBP/FB-story "not measurable" rule.
//
// ═══ THE AVAILABILITY MATRIX (mutation-tested) ═══
//   metric \ platform  facebook          instagram              googlebusiness
//   followers          page_follows ✓    follower-history ✓*    ✗ no analytics
//   reach              page_media_view ✓ reach ✓                ✗ no analytics
//   engagement         page_post_eng ✓   ✗ total_value only     ✗ no analytics
//   posts              cadence ✓         cadence ✓              cadence ✓
//   * IG followers are fetched by paging the endpoint's 89-day per-query window and
//     stitching (Zernio retains daily counts indefinitely) — the line covers the
//     full selected period, no cap caveat.
// A combination with no daily series draws NO line and its legend greys with the
// reason — never a flat zero line (zero and "no measuring instrument" differ).

export type ExplorerMetric = 'followers' | 'reach' | 'views' | 'engagement' | 'posts';
export type ExplorerChartType = 'line' | 'bar' | 'area';

export const EXPLORER_METRICS: Array<{
  key: ExplorerMetric;
  label: string;
  /** Posts (small counts) get the right axis; continuous metrics share the left. */
  axis: 'left' | 'right';
  defaultChart: ExplorerChartType;
}> = [
  { key: 'followers', label: 'Followers', axis: 'left', defaultChart: 'line' },
  { key: 'reach', label: 'Reach', axis: 'left', defaultChart: 'line' },
  { key: 'views', label: 'Views', axis: 'left', defaultChart: 'line' },
  { key: 'engagement', label: 'Engagement', axis: 'left', defaultChart: 'line' },
  { key: 'posts', label: 'Posts published', axis: 'right', defaultChart: 'bar' },
];

// Colour is the PLATFORM (a chart-legible palette; ChannelIcon's slate GBP is too
// low-contrast for a data line, so GBP gets a real green here).
export const PLATFORM_COLOR: Record<SocialPlatform, string> = {
  facebook: '#1877F2',
  instagram: '#E1306C',
  googlebusiness: '#0F9D58',
};

// Line STYLE is the METRIC (so two same-colour lines for one platform stay
// readable). Bars carry no dash. Kept as a recharts strokeDasharray string.
export const METRIC_DASH: Record<ExplorerMetric, string | undefined> = {
  followers: undefined, // solid
  reach: '6 4', // dashed
  views: '1 3', // fine dots
  engagement: '2 3', // dotted
  posts: undefined, // bars
};

export const PLATFORM_LABEL: Record<SocialPlatform, string> = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  googlebusiness: 'Google Business',
};

const METRIC_LABEL: Record<ExplorerMetric, string> = {
  followers: 'Followers',
  reach: 'Reach',
  views: 'Views',
  engagement: 'Engagement',
  posts: 'Posts published',
};

const NO_ANALYTICS = 'Google Business has no reach, engagement or follower analytics';
// IG `engagement` (total_interactions) AND `views` are total_value-ONLY on the
// account-insights endpoint (a time_series request 400s) — so neither draws a
// daily LINE; both live as period totals on the IG card instead.
const IG_ENGAGEMENT_TOTAL_ONLY = 'Instagram engagement is a period total, not a daily series';
const IG_VIEWS_TOTAL_ONLY = 'Instagram views is a period total, not a daily series';
const NOT_CONNECTED = 'Not connected';

// ---------------------------------------------------------------------------
// the availability gate
// ---------------------------------------------------------------------------

export interface MetricPlatformAvailability {
  available: boolean;
  reason: string | null;
}

/**
 * Whether a (metric, platform) pair has a daily series to draw. THE gate — every
 * "no line drawn" and every greyed legend entry flows from here.
 */
export function metricPlatformAvailability(
  metric: ExplorerMetric,
  platform: SocialPlatform,
  connected: Record<SocialPlatform, boolean>,
): MetricPlatformAvailability {
  if (!connected[platform]) return { available: false, reason: NOT_CONNECTED };

  // Posts cadence is available wherever the platform is connected (native +
  // mirror history covers FB / IG / GBP alike).
  if (metric === 'posts') return { available: true, reason: null };

  // Google Business has no analytics surface at all beyond cadence.
  if (platform === 'googlebusiness') return { available: false, reason: NO_ANALYTICS };

  // Instagram engagement + views are total_value-only (a daily series 400s) — no
  // line; they surface as period totals on the IG card instead.
  if (platform === 'instagram' && metric === 'engagement') {
    return { available: false, reason: IG_ENGAGEMENT_TOTAL_ONLY };
  }
  if (platform === 'instagram' && metric === 'views') {
    return { available: false, reason: IG_VIEWS_TOTAL_ONLY };
  }

  // FB followers/reach/views/engagement + IG followers/reach all have daily series.
  return { available: true, reason: null };
}

// ---------------------------------------------------------------------------
// per-day value extraction
// ---------------------------------------------------------------------------

function inRange(date: string, range: SocialDateRange): boolean {
  return date >= range.from && date <= range.to;
}

/** Enumerate every calendar day in an inclusive YYYY-MM-DD range. */
export function enumerateDays(range: SocialDateRange): string[] {
  const out: string[] = [];
  let t = Date.parse(`${range.from}T00:00:00Z`);
  const end = Date.parse(`${range.to}T00:00:00Z`);
  if (Number.isNaN(t) || Number.isNaN(end)) return out;
  while (t <= end) {
    out.push(new Date(t).toISOString().slice(0, 10));
    t += 86_400_000;
  }
  return out;
}

/** date → value map for a continuous daily series, restricted to the window. */
function seriesDayMap(series: SocialMetricSeries | null, range: SocialDateRange): Map<string, number> {
  const m = new Map<string, number>();
  if (!series) return m;
  for (const p of series.values) if (inRange(p.date, range)) m.set(p.date, p.value);
  return m;
}

/** date → post COUNT for a platform's published posts, restricted to the window. */
function postCountDayMap(
  data: SocialOverview,
  platform: SocialPlatform,
  range: SocialDateRange,
): Map<string, number> {
  const m = new Map<string, number>();
  for (const post of data.publishedPosts) {
    if (post.platform !== platform) continue;
    const day = post.publishedAt.slice(0, 10);
    if (!inRange(day, range)) continue;
    m.set(day, (m.get(day) ?? 0) + 1);
  }
  return m;
}

// The daily series backing one (platform, metric). Continuous metrics return null
// on a day with no snapshot (a gap, connectNulls); posts return the count (0 is a
// real "published nothing", so it stays 0, drawing an empty bar).
function dayMapFor(
  data: SocialOverview,
  metric: ExplorerMetric,
  platform: SocialPlatform,
  range: SocialDateRange,
): { map: Map<string, number>; fillZero: boolean } {
  if (metric === 'posts') return { map: postCountDayMap(data, platform, range), fillZero: true };
  if (platform === 'facebook' && data.facebook) {
    // ⚠️ FB Views and FB Reach both derive from page_media_view — the only
    // CONFIRMED daily FB impressions/views metric (Meta unified impressions +
    // video-plays into "views" post-Nov-2025; page_media_view "replaces
    // page_impressions"). Reach is the historical proxy over the same series, so
    // the two FB lines coincide. page_video_views exists in the SDK docs but is
    // UNPROBED, so we don't rely on it.
    const series =
      metric === 'followers'
        ? data.facebook.pageFollows
        : metric === 'reach' || metric === 'views'
          ? data.facebook.pageMediaView
          : data.facebook.pagePostEngagements;
    return { map: seriesDayMap(series, range), fillZero: false };
  }
  if (platform === 'instagram' && data.instagram) {
    // IG followers + reach are the only IG daily series; engagement + views are
    // total_value-only (gated out above, so never reached here).
    const series =
      metric === 'followers'
        ? data.instagram.followerCount
        : metric === 'reach'
          ? data.instagram.reach
          : null;
    return { map: seriesDayMap(series, range), fillZero: false };
  }
  return { map: new Map(), fillZero: false };
}

// ---------------------------------------------------------------------------
// series + row assembly
// ---------------------------------------------------------------------------

export interface ExplorerSeries {
  /** Row datakey: `${platform}_${metric}` (underscore — dots break the chart's
   *  `--color-<key>` CSS var). */
  key: string;
  platform: SocialPlatform;
  metric: ExplorerMetric;
  label: string; // "Facebook · Reach"
  color: string;
  dash: string | undefined;
  axis: 'left' | 'right';
  chartType: ExplorerChartType;
}

export interface ExplorerData {
  rows: Array<Record<string, number | null | string>>;
  series: ExplorerSeries[];
  /** Any active series uses the right (posts) axis. */
  hasRightAxis: boolean;
}

export interface ExplorerOptions {
  activeMetrics: ExplorerMetric[];
  activePlatforms: SocialPlatform[];
  chartTypes: Record<ExplorerMetric, ExplorerChartType>;
}

const METRIC_ORDER: ExplorerMetric[] = ['followers', 'reach', 'views', 'engagement', 'posts'];
const PLATFORM_ORDER: SocialPlatform[] = ['facebook', 'instagram', 'googlebusiness'];

/**
 * Assemble the chart's series + row data for the current period. Only AVAILABLE
 * (metric × platform) combinations among the active selections become series; the
 * rest simply don't appear (their absence is explained by the legend).
 */
export function buildExplorerData(data: SocialOverview, opts: ExplorerOptions): ExplorerData {
  const days = enumerateDays(data.range);
  const activeMetrics = new Set(opts.activeMetrics);
  const activePlatforms = new Set(opts.activePlatforms);

  const series: ExplorerSeries[] = [];
  const maps = new Map<string, { map: Map<string, number>; fillZero: boolean }>();

  for (const metric of METRIC_ORDER) {
    if (!activeMetrics.has(metric)) continue;
    const chartType = opts.chartTypes[metric] ?? 'line';
    const axis = EXPLORER_METRICS.find((m) => m.key === metric)!.axis;
    for (const platform of PLATFORM_ORDER) {
      if (!activePlatforms.has(platform)) continue;
      if (!metricPlatformAvailability(metric, platform, data.connected).available) continue;
      const key = `${platform}_${metric}`;
      series.push({
        key,
        platform,
        metric,
        label: `${PLATFORM_LABEL[platform]} · ${METRIC_LABEL[metric]}`,
        color: PLATFORM_COLOR[platform],
        dash: METRIC_DASH[metric],
        axis,
        chartType,
      });
      maps.set(key, dayMapFor(data, metric, platform, data.range));
    }
  }

  const rows = days.map((date) => {
    const row: Record<string, number | null | string> = { date };
    for (const s of series) {
      const cell = maps.get(s.key)!;
      const v = cell.map.get(date);
      row[s.key] = v ?? (cell.fillZero ? 0 : null);
    }
    return row;
  });

  return {
    rows,
    series,
    hasRightAxis: series.some((s) => s.axis === 'right'),
  };
}

// ---------------------------------------------------------------------------
// platform legend (clickable toggles)
// ---------------------------------------------------------------------------

export interface PlatformLegendEntry {
  platform: SocialPlatform;
  label: string;
  color: string;
  connected: boolean;
  /** Has ≥1 available series among the ACTIVE metrics → the toggle is live. */
  enabled: boolean;
  /** Why the toggle is greyed (aggregate over the active metrics). */
  reason: string | null;
}

/**
 * The bottom legend, one entry per connected platform. A platform is "enabled"
 * (toggleable) when at least one currently-active metric has a drawable series for
 * it — so with only Reach + Engagement active, Google Business greys with its
 * reason, and it lights up the moment Posts Published is ticked.
 */
export function buildPlatformLegend(
  data: SocialOverview,
  activeMetrics: ExplorerMetric[],
): PlatformLegendEntry[] {
  return PLATFORM_ORDER.filter((p) => data.connected[p]).map((platform) => {
    let enabled = false;
    let reason: string | null = null;
    for (const metric of activeMetrics) {
      const a = metricPlatformAvailability(metric, platform, data.connected);
      if (a.available) {
        enabled = true;
        reason = null;
        break;
      }
      reason ??= a.reason;
    }
    if (activeMetrics.length === 0) reason = 'No metrics selected';
    return {
      platform,
      label: PLATFORM_LABEL[platform],
      color: PLATFORM_COLOR[platform],
      connected: true,
      enabled,
      reason: enabled ? null : reason,
    };
  });
}
